import { useMemo } from "react";
import { PREMIUM_FEATURES } from "@common/flatRateBilling";
import { isEnumMember } from "@common/helpers/typeGuards";
import { DEFAULT_SCHEDULES_REPORT_LAYOUT } from "../schedulesReport/useSchedulesReportParams";
import { formatISO, parseISO } from "date-fns";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getAccountingBasisLabel } from "@common/helpers/accountingBasis";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    isArAgingReportPackageType,
    isAccrualReportPackageType,
    ReportPackageRowReportType,
    ReportPackageReportType,
} from "@common/types/reports/report-package";
import {
    ACCOUNTING_REPORT_LABELS,
    AccountingReportType,
} from "@common/types/reports/report-type";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { Filters } from "@components/pageHeader/filters/Filters";
import { FilterTabs } from "@components/pageHeader/filters/lib";
import { useAllowFeature } from "@hooks/useAllowFeature";
import { Button } from "@design-system/Button/Button";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { useConstant } from "@hooks/useConstant";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import type { DateOnlyDateRange } from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import { Field } from "@design-system/Field/Field";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { Input } from "@design-system/Input/Input";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { ACCOUNTING_REPORT_FILTER_DEFINITIONS } from "../accountingReportFilterDefinitions";
import {
    getAccountingReportAvailableFilters,
    getAccountingReportFilterOverlayProps,
    mapSchedulesReportFilters,
} from "../accountingReportFilters.lib";
import { CloseIcon, DragHandle6DotIcon } from "@icons";
import {
    REPORT_PACKAGE_ACCRUAL_UNAVAILABLE_TOOLTIP,
    resolveReportPackageLedgerId,
} from "../ReportPackageModal/reportPackageBasis.lib";
import { ArAgingAsOfDateFilter } from "../arAging/ArAgingAsOfDateFilter";
import {
    getCustomReportPackageRowDatesPatch,
    getReportPackageRowEntityFilters,
    ReportPackageRowOptions,
    ReportPackageRow,
} from "./reportPackageRows.lib";
import { ReportPackageBuilderRowOptions } from "./ReportPackageBuilderRowOptions";
import styles from "./ReportPackageBuilderDrawer.module.scss";
import { useCanUseRevenueRecognition } from "@hooks/useCanUseRevenueRecognition";
import { useSchedulesReportDateBoundsQuery } from "@api/schedulesReport.api";
import {
    SchedulesReportLayout,
    SCHEDULES_REPORT_LAYOUT_LABELS,
} from "@common/types/reports/schedules-report";
import { buildSchedulesReportExtraPresets } from "../schedulesReport/schedulesReportDatePresets";

interface Props {
    row: ReportPackageRow;
    selectedEntityIds: number[];
    ledgersByEntityId: Parameters<
        typeof resolveReportPackageLedgerId
    >[0]["ledgersByEntityId"];
    isEntityPerRow: boolean;
    availableEntityIds: number[];
    reportTypes: ReportPackageReportType[];
    canGroupByClass: boolean;
    onOptionsChange: (options: ReportPackageRowOptions) => void;
    packageDateRange: DateOnlyDateRange;
    pinnedPresets: readonly string[];
    onTogglePin: (id: string) => void;
    onUpdate: (patch: Partial<Omit<ReportPackageRow, "id" | "title">>) => void;
    isAccrualAvailable: boolean;
    onReportTypeChange: (
        reportType: ReportPackageRowReportType,
        layout?: SchedulesReportLayout,
    ) => void;
    onBasisChange: (basis: AccountingLedgerBasis) => void;
    onTitleChange: (title: string) => void;
    titleError?: string;
    onRemove: () => void;
}

export const ReportPackageBuilderRow = ({
    row,
    selectedEntityIds,
    ledgersByEntityId,
    isEntityPerRow,
    availableEntityIds,
    reportTypes,
    canGroupByClass,
    onOptionsChange,
    packageDateRange,
    pinnedPresets,
    onTogglePin,
    onUpdate,
    isAccrualAvailable,
    onReportTypeChange,
    onBasisChange,
    onTitleChange,
    titleError,
    onRemove,
}: Props) => {
    const { hasAccess: hasClassesAccess } = useAllowFeature(
        PREMIUM_FEATURES.CLASSES,
    );
    const isSchedules = row.reportType === AccountingReportType.SCHEDULES;
    const filterDefinition =
        ACCOUNTING_REPORT_FILTER_DEFINITIONS[row.reportType];
    const filterProps = isSchedules
        ? {
              showCounterpartyFilter: true,
              showAmountFilter: true,
              showSchedulesReportFilters: true,
          }
        : filterDefinition?.filterProps;
    const availableFilters = filterProps
        ? getAccountingReportAvailableFilters({
              ...filterProps,
              hasClassesAccess,
          }).filter((tab) => !isSchedules || tab !== FilterTabs.HOLDING_ACCOUNT)
        : [];
    const {
        attributes: sortableAttributes,
        listeners: sortableListeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({ id: row.id });
    const canUseSchedules = useCanUseRevenueRecognition();
    const reportItems = useMemo<
        Array<PickerItemData<ReportPackageReportType | SchedulesReportLayout>>
    >(
        () => [
            ...reportTypes.map((reportType) => ({
                value: reportType,
                label: ACCOUNTING_REPORT_LABELS[reportType],
                disabled:
                    isAccrualReportPackageType(reportType) &&
                    !isAccrualAvailable,
                disabledReason:
                    isAccrualReportPackageType(reportType) &&
                    !isAccrualAvailable
                        ? REPORT_PACKAGE_ACCRUAL_UNAVAILABLE_TOOLTIP
                        : undefined,
            })),
            ...(canUseSchedules
                ? Object.values(SchedulesReportLayout).map((layout) => ({
                      value: layout,
                      label: SCHEDULES_REPORT_LAYOUT_LABELS[layout],
                      disabled: !isAccrualAvailable,
                      disabledReason: !isAccrualAvailable
                          ? REPORT_PACKAGE_ACCRUAL_UNAVAILABLE_TOOLTIP
                          : undefined,
                  }))
                : []),
        ],
        [reportTypes, isAccrualAvailable, canUseSchedules],
    );
    const layout = row.layout ?? DEFAULT_SCHEDULES_REPORT_LAYOUT;
    const rowEntityIds = row.entityId === undefined ? [] : [row.entityId];
    const reportEntityIds = isEntityPerRow ? rowEntityIds : selectedEntityIds;
    const rowLedgerId = resolveReportPackageLedgerId({
        ledgersByEntityId,
        selectedEntityIds: reportEntityIds,
        basis: row.basis,
    });
    const dateBounds = useSchedulesReportDateBoundsQuery(
        {
            entityId: reportEntityIds[0],
            entityIds: reportEntityIds,
            ledgerId: rowLedgerId,
            classes: row.filters?.classes,
            classFilterType: row.filters?.classFilterType,
        },
        {
            ...mapSchedulesReportFilters(row.filters),
            revenueSubMode: row.revenueSubMode,
            method: row.method,
        },
        {
            isEnabled:
                isSchedules &&
                canUseSchedules &&
                isAccrualAvailable &&
                !row.usesPackageDates,
        },
    );
    const today = useConstant(() => new Date());
    const extraDatePresets = useMemo(
        () =>
            isSchedules
                ? buildSchedulesReportExtraPresets({
                      today,
                      dateBounds: dateBounds.data,
                  })
                : undefined,
        [isSchedules, today, dateBounds.data],
    );
    const reportValue =
        row.reportType === AccountingReportType.SCHEDULES
            ? layout
            : row.reportType;
    const reportLabel = isSchedules
        ? SCHEDULES_REPORT_LAYOUT_LABELS[layout]
        : ACCOUNTING_REPORT_LABELS[row.reportType];
    const basisItems = [
        AccountingLedgerBasis.cash,
        AccountingLedgerBasis.accruals,
    ].map((basis) => {
        const isUnavailable =
            basis === AccountingLedgerBasis.accruals && !isAccrualAvailable;
        return {
            value: basis,
            label: getAccountingBasisLabel(basis),
            disabled: isUnavailable,
            disabledReason: isUnavailable
                ? REPORT_PACKAGE_ACCRUAL_UNAVAILABLE_TOOLTIP
                : undefined,
        };
    });
    const isArAging = isArAgingReportPackageType(row.reportType);
    const isAccrualUnavailable =
        (isAccrualReportPackageType(row.reportType) ||
            row.basis === AccountingLedgerBasis.accruals) &&
        !isAccrualAvailable;
    const accrualError = isAccrualUnavailable
        ? REPORT_PACKAGE_ACCRUAL_UNAVAILABLE_TOOLTIP
        : undefined;
    const accessibleTitle = row.title.trim() || reportLabel;

    return (
        <div
            ref={setNodeRef}
            className={styles.row}
            style={{
                transform: CSS.Translate.toString(transform),
                transition,
            }}
        >
            <VStack gap={12} paddingBlock={8} paddingInline={12}>
                <HStack gap={12} align="start">
                    <Button
                        type="button"
                        variant="secondary"
                        size="md"
                        iconOnly
                        {...sortableListeners}
                        {...sortableAttributes}
                        aria-label={`Reorder ${accessibleTitle}`}
                    >
                        <DragHandle6DotIcon />
                    </Button>
                    <Picker.Select
                        searchable
                        items={reportItems}
                        value={reportValue}
                        onValueChange={(value) => {
                            if (value && value !== reportValue) {
                                if (
                                    isEnumMember(SchedulesReportLayout)(value)
                                ) {
                                    onReportTypeChange(
                                        AccountingReportType.SCHEDULES,
                                        value,
                                    );
                                } else {
                                    onReportTypeChange(value);
                                }
                            }
                        }}
                        trigger={
                            <Picker.Button
                                variant="primary"
                                size="md"
                                aria-label="Report"
                                className={styles.reportTrigger}
                            />
                        }
                    />
                    <Field
                        label="Title"
                        labelHidden
                        error={titleError}
                        className={styles.rowTitle}
                    >
                        <Input
                            value={row.title}
                            onChange={(event) =>
                                onTitleChange(event.target.value)
                            }
                            maxLength={31}
                        />
                    </Field>
                    <Field
                        label="Basis"
                        labelHidden
                        control="button"
                        error={accrualError}
                        className={styles.basisField}
                    >
                        {isAccrualReportPackageType(row.reportType) ? (
                            <span className={styles.basisValue}>
                                {getAccountingBasisLabel(
                                    AccountingLedgerBasis.accruals,
                                )}
                            </span>
                        ) : (
                            <Picker.Select
                                items={basisItems}
                                value={row.basis}
                                onValueChange={(value) => {
                                    if (value) {
                                        onBasisChange(value);
                                    }
                                }}
                                trigger={
                                    <Picker.Button
                                        variant="primary"
                                        size="md"
                                        aria-label="Basis"
                                        className={styles.basisTrigger}
                                    />
                                }
                            />
                        )}
                    </Field>
                    <Button
                        type="button"
                        variant="secondary"
                        size="md"
                        iconOnly
                        aria-label={`Remove ${accessibleTitle}`}
                        onClick={onRemove}
                    >
                        <CloseIcon />
                    </Button>
                </HStack>
                <HStack gap={12} align="center" wrap>
                    {isEntityPerRow && (
                        <Field
                            label="Entity"
                            labelHidden
                            control="button"
                            className={styles.entityField}
                        >
                            <EntityFilterCombobox
                                filters={{
                                    entityIds:
                                        row.entityId === undefined
                                            ? []
                                            : [row.entityId],
                                }}
                                onChange={({ entityIds }) => {
                                    const entityId = entityIds?.[0];
                                    onUpdate({
                                        entityId,
                                        filters:
                                            getReportPackageRowEntityFilters({
                                                row,
                                                previousEntityId: row.entityId,
                                                entityId,
                                            }),
                                    });
                                }}
                                selectionMode="single"
                                triggerButtonProps={{
                                    className: styles.triggerButton,
                                }}
                                mode="selection"
                                onlyBusiness
                                excludeAllMockEntities
                                isEntityDisabled={(entity) =>
                                    !availableEntityIds.includes(entity.id)
                                }
                            />
                        </Field>
                    )}
                    {!row.usesPackageDates &&
                        (isArAging ? (
                            <ArAgingAsOfDateFilter
                                value={
                                    row.endDate
                                        ? parseISO(row.endDate)
                                        : undefined
                                }
                                onChange={(value) => {
                                    const date = value
                                        ? formatISO(value, {
                                              representation: "date",
                                          })
                                        : undefined;
                                    onUpdate({
                                        startDate: date,
                                        endDate: date,
                                    });
                                }}
                            />
                        ) : (
                            <DateRangeFilterNext
                                dateRange={{
                                    start: row.startDate,
                                    end: row.endDate,
                                }}
                                onChange={({ start, end }) =>
                                    onUpdate({ startDate: start, endDate: end })
                                }
                                requireSelection
                                extraPresets={extraDatePresets}
                                pinnedPresets={pinnedPresets}
                                onTogglePin={onTogglePin}
                                buttonClass={styles.rowDateTrigger}
                            />
                        ))}
                    <Checkbox
                        checked={row.usesPackageDates}
                        label="Same as package"
                        onCheckedChange={(usesPackageDates) =>
                            onUpdate(
                                usesPackageDates
                                    ? { usesPackageDates: true }
                                    : getCustomReportPackageRowDatesPatch({
                                          row,
                                          packageDateRange,
                                      }),
                            )
                        }
                    />
                </HStack>
                <ReportPackageBuilderRowOptions
                    row={row}
                    canGroupByClass={canGroupByClass}
                    onOptionsChange={onOptionsChange}
                    onUpdate={onUpdate}
                />
                {availableFilters.length > 0 && (
                    <HStack gap={8} align="center" wrap>
                        <Filters
                            filters={row.filters ?? {}}
                            onChange={(filters) =>
                                onUpdate({
                                    filters: { ...row.filters, ...filters },
                                })
                            }
                            availableFilters={availableFilters}
                            {...getAccountingReportFilterOverlayProps(
                                filterDefinition?.filterProps,
                            )}
                            hardcodedEntityId={reportEntityIds[0]}
                            defaultTab={availableFilters[0]}
                        />
                    </HStack>
                )}
            </VStack>
        </div>
    );
};
